import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { SaleFormService } from '@app/features/sales/add-sale/steps/sale-form.service';
import { CompanyService, ItemResponse, ItemsService, ProductsService, ProductDetailsSalesWizardResponse, CompanyResponse, GenericRequestByRetailer, ProductAttributeDefinitionsService, ProductAttributeDefinitionDetailsMinimalResponse, FilterRequest, ProductCustomisationDetailsResponse, SettingsService } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { firstValueFrom } from 'rxjs';
import { WarrantyActivationMethod } from '@app/shared/enums/WarrantyActivationMethod.enum';
import { PermissionService } from "@src/app/shared/services/permission.service";
import { SessionService } from '@src/app/shared/services/session.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { NoticePlacement } from '@src/app/shared/enums/NoticePlacement.enum';
import { NoticeType } from '@src/app/shared/enums/NoticeType.enum';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-serial-number-validation',
    templateUrl: './serial-number-validation.component.html',
    styleUrl: './serial-number-validation.component.scss',
    host: {
        class: 'app-serial-number-validation'
    },
    standalone: false
})
export class StepSerialNumberValidationComponent implements OnInit {

  company: CompanyResponse = {
    inventorySubmissionThresholdDays: 0
  };

  hasAssistanceSerialNumberImage: boolean = false;
  assistanceSerialNumberImagePath: string | null = null;
  assistanceSerialNumberImage: string | null = '';
  productAttributeDefinitions: ProductAttributeDefinitionDetailsMinimalResponse[] = [];
  translatedProductAttributes: { code: string, name: string; value: string, changesOverTime: boolean }[] = [];

  displayItemModalBox: boolean = false;
  displayItemCreationButton: boolean = false;
  productAttributesFeatureEnabled: boolean = false;
  noticeMessage: string | null = '';
  noticeType: NoticeType = NoticeType.INFORMATION;

  tooltipImage: string = '';
  isSerialNumberValid: boolean = false;
  identifierError: string | null = null;

  saleId = null;
  validationSearchResults: any[] = [];
  isFormSubmitted: boolean = false;
  isResultsEmpty: boolean = true;

  item: ItemResponse = {};
  itemsList: any;
  currentItem: any;
  currentSku: any;

  product: ProductDetailsSalesWizardResponse | null = null;

  inMemoryPagedResults: any[] = [];
  inMemoryTotalRecords: number = 0;
  inMemoryPaginator = { first: 0, rows: 5 };

  saleType = WarrantyActivationMethod;
  selectedSaleType: any;

  isLimitedEditionValid: boolean = false;
  showValues: boolean = false;

  modalProductOptions: any[] = [];
  modalProductSelectorDisabled: boolean = false;

  modalSerialDisabled: boolean = true;

  referenceProductRequiresSerialNumber: boolean = true;

  get isReferenceSaleType(): boolean {
    return this.selectedSaleType === this.saleType.REFERENCE_NUMBER;
  }

  @Output() stepSaved = new EventEmitter<void>();

  combosData: { [key: string]: ComboData } = {
    'products': new ComboData(async (filter) => await firstValueFrom(this.productsService.apiProductsFilterComboPost(filter))),
    'items': new ComboData(async (filter) => await firstValueFrom(this.itemsService.apiItemsFilterComboPost(filter)))
  }

  async onLazyLoadComboData(comboType: 'products' | 'items', event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    if (comboType === 'items') {
      if (!this.currentSku) {
        this.combosData['items'].reset();
      }
      else {
        await this.combosData['items'].getData(search, activePage);
      }

      this.syncEditionNumberControlState();
      return;
    }

    if (!this.configureProductsFilterByRetailerBrands()) {
      return;
    }

    await this.combosData['products'].getData(search, activePage);
    this.modalProductOptions = this.combosData['products'].list || [];

  }

  async onProductsLazyLoad(event: any) {
    // disabled selector to prevent fetch/change
    if (this.modalProductSelectorDisabled) return;

    await this.onLazyLoadComboData('products', event);
  }

  searchSerialNumberFormGroup: FormGroup = this.fb.group({
    serialNumber: ['', [Validators.required]],
    item: [null]
  });

  searchSkuFormGroup: FormGroup = this.fb.group({
    sku: ['', [Validators.required]],
    item: [null],
    editionNumber: [[]]
  });

  itemForm: FormGroup = this.fb.group({
    productId: [[], [Validators.required]],
    serialNumber: ['', [Validators.required, Validators.maxLength(50)]],
    implicitCodePairing: ['true'],
    warrantyCode: [null, [Validators.maxLength(50)]]
  });

  constructor(
    public session: SessionService,
    private fb: FormBuilder,
    private translate: TranslateService,
    private companyService: CompanyService,
    private itemsService: ItemsService,
    private productsService: ProductsService,
    private saleFormService: SaleFormService,
    private productAttributeDefinitionsService: ProductAttributeDefinitionsService,
    private settingsService: SettingsService,
    private permisionService: PermissionService) { }

  async ngOnInit() {
    this.company = await this.session.getCompanyData();
    const companySettings = await this.session.getCompanySettings();
    this.productAttributesFeatureEnabled = companySettings.features?.some(f => f.name === 'ProductAttributes' && f.isEnabled) ?? false;
    const noticePlacement = NoticePlacement.SALE_ADD_PRODUCT_IDENTIFIER_SN;
    const response = await firstValueFrom(this.settingsService.apiSettingsNoticesPlacementPlacementIdGet(noticePlacement));
    if(response != null)
    {
      const user = await this.session.getUser();
      this.noticeMessage =  response?.noticeTranslations?.find( x => x.isoCode === user?.language?.code)?.translation
                                                    || response?.noticeTranslations?.find( x => x.isoCode === 'en')?.translation  || null;

      this.noticeType = NoticeType[response.type as keyof typeof NoticeType] ?? NoticeType.INFORMATION;
    }
    this.getAssistanceSerialNumberImage();
    this.initFormStep();
  }

  qrCodeScanned(data: any) {
    this.searchSerialNumberFormGroup.controls['serialNumber'].setValue(data, { emitEvent: false });
  }

  async initFormStep() {
    const stepData = this.saleFormService.getStepData('identifier');
    this.selectedSaleType = this.saleFormService.getStepData('saleType')?.saleType || null;

    if (!stepData) return;

    const rememberedSku = stepData.sku ?? stepData?.item?.product?.sku ?? '';
    const rememberedSn = stepData.serialNumber ?? '';

    const rememberedItem = stepData.item;

    // force to select a result
    this.currentItem = null;
    this.isSerialNumberValid = false;

    this.isFormSubmitted = true;
    this.identifierError = null;
    this.showValues = !!rememberedItem;
    this.isLimitedEditionValid = false;
    this.displayItemCreationButton = false;

    this.searchSerialNumberFormGroup.patchValue({
      serialNumber: rememberedSn,
      item: null
    });

    const rememberedEditionNumber = Array.isArray(stepData.editionNumber)
      ? stepData.editionNumber
      : stepData.editionNumber?.id != null
        ? [stepData.editionNumber.id]
        : [];

    this.searchSkuFormGroup.patchValue({
      sku: rememberedSku,
      item: null,
      editionNumber: rememberedEditionNumber
    });

    // overwrite memory to force result selection
    const newStepData = {
      ...stepData,
      sku: rememberedSku,
      serialNumber: rememberedSn,
      item: null,
      itemId: null,
      productCustomisationId: null
    };
    this.saleFormService.setStepData('identifier', newStepData);

    // display results
    if (rememberedItem) {
      this.validationSearchResults = await this.buildDisplayResults([rememberedItem]);
      this.isResultsEmpty = this.validationSearchResults.length === 0;
      this.resetInMemoryPaginatorAndSlice();
    } else {
      this.validationSearchResults = [];
      this.isResultsEmpty = true;
      this.updateInMemoryPageSlice();
    }

    this.syncEditionNumberControlState();
  }

  displayTooltipImage(): boolean {
    return !!(this.tooltipImage = this.assistanceSerialNumberImage || '');
  }

  getAssistanceSerialNumberImage() {
    this.session.currentCompanyData$.subscribe(async company => {
      const path = company?.assistanceImagePath ?? null;
      const hasImg = !!path;

      this.hasAssistanceSerialNumberImage = hasImg;
      this.assistanceSerialNumberImagePath = path;

      if (hasImg) {
        try {
          const resp = await firstValueFrom(this.companyService.apiCompanyAssistanceImageGet());
          const base64 = resp?.response ?? null;
          this.assistanceSerialNumberImage = base64;
        } catch (error) {
          console.error('Error fetching assistance image:', error);
        }
      } else {
        this.assistanceSerialNumberImage = null;
      }
    });
  }

  private async buildDisplayResults(items: any[]): Promise<any[]> {
    if (!items || items.length === 0) return [];

    const allLimitedEdition = items.every(i => i?.product?.isLimitedEdition);
    const hasAnyCustomisableWithOptions = items.some(
      i => i?.product?.isCustomisable && (i?.product?.productCustomisations?.length > 0)
    );

    // If limited edition & not customisable
    // multiple items-> display list || 1 item -> auto-advance
    if (allLimitedEdition && !hasAnyCustomisableWithOptions) {
      return items.length === 1 ? [items[0]] : items;
    }
    if(hasAnyCustomisableWithOptions)
    {
      await this.getProductAttributesDefinitions();
      this.fillTranslatedAttributeNames();
    }

    // Otherwise: expand customisable items
    const results: any[] = [];
    items.forEach(item => {
      const product = item?.product;
      const customisations = product?.productCustomisations || [];

      if (product?.isCustomisable && customisations.length > 0) {
        customisations.forEach((customisation: any) => {
          results.push({
            ...item,
            productCustomisation: customisation,
            productCustomisationId: customisation?.id,
          });
        });
      } else {
        // Regular product
        results.push(item);
      }
    });

    return results;
  }

  private updateInMemoryPageSlice() {
    const src = this.validationSearchResults || [];
    this.inMemoryTotalRecords = src.length;
    const { first, rows } = this.inMemoryPaginator;
    this.inMemoryPagedResults = src.slice(first, first + rows);
    this.isResultsEmpty = this.inMemoryTotalRecords === 0;
  }

  private resetInMemoryPaginatorAndSlice() {
    this.inMemoryPaginator.first = 0;
    this.updateInMemoryPageSlice();
  }

  private syncEditionNumberControlState(): void {
    const control = this.searchSkuFormGroup.get('editionNumber');
    if (!control) return;

    const shouldDisable = (this.combosData['items'].list?.length ?? 0) < 2;

    if (shouldDisable) {
      control?.disable({ emitEvent: false });
    } else {
      control?.enable({ emitEvent: false });
    }
  }

  private syncModalProductControlState(): void {
    const control = this.itemForm.get('productId');
    if (!control) return;

    if (this.modalProductSelectorDisabled) {
      control?.disable({ emitEvent: false });
    } else {
      control?.enable({ emitEvent: false });
    }
  }

  onInMemoryPageChange(event: any) {
    this.inMemoryPaginator.first = event.first ?? 0;
    this.inMemoryPaginator.rows = event.rows ?? this.inMemoryPaginator.rows;
    this.updateInMemoryPageSlice();
  }

  displaySerialNumber() {
    return !this.company.hasLimitedEdition || this.selectedSaleType !== this.saleType.REFERENCE_NUMBER;
  }

  getComboTotalPages(comboType: 'products' | 'items'): number {
    const totalRecords = this.combosData[comboType]?.totalRecords ?? 0;
    const pageSize = Number(this.combosData[comboType]?.rowsPerPageOption) || 5;

    return Math.max(1, Math.ceil(totalRecords / pageSize));
  }

  private setItemFormValidatorsForCurrentSaleType(): void {
    const serialCtrl = this.itemForm.get('serialNumber');
    if (!serialCtrl) return;

    if (this.isReferenceSaleType) {
      // REFERENCE flow: require SN if product says so
      if (this.referenceProductRequiresSerialNumber) {
        serialCtrl.setValidators([Validators.required, Validators.maxLength(50)]);
      } else {
        serialCtrl.setValidators([Validators.maxLength(50)]);
      }
    } else {
      // SN flow: always require SN
      serialCtrl.setValidators([Validators.required, Validators.maxLength(50)]);
    }

    serialCtrl.updateValueAndValidity();
  }

  private extractCreatedId(resp: any): number | null {
    if (resp === null || resp === undefined) return null;
    if (typeof resp === 'number') return resp;
    if (typeof resp === 'string' && resp.trim() !== '' && !isNaN(Number(resp))) return Number(resp);
    return resp?.id ?? resp?.itemId ?? resp?.response ?? null;
  }

  async onValidateSerialNumber() {
    try {
      this.identifierError = null;
      this.displayItemCreationButton = false;
      const payload: GenericRequestByRetailer = {
        request: this.searchSerialNumberFormGroup.controls['serialNumber'].value,
        retailerId: this.saleFormService.getStepData('sellerData').retailerId
      };
      const itemsPage = await firstValueFrom(this.itemsService.apiItemsSearchSerialNumberPost(payload));
      this.itemsList = itemsPage.results || [];
      this.isFormSubmitted = true;

      if (this.itemsList.length === 0) {
        this.identifierError = this.translate.instant('SALE_CREATE.VALIDATE_SERIAL_NUMBER');
        this.isSerialNumberValid = false;
        this.validationSearchResults = [];
        this.isResultsEmpty = true;
        this.updateInMemoryPageSlice();

        if (this.company.canCreateItemOnSale && (await this.permisionService.hasActionPermissions(Actions.create_sales))) {
          this.itemForm.patchValue({
            serialNumber: this.searchSerialNumberFormGroup.controls['serialNumber'].value
          });
          this.displayItemCreationButton = true;
        }
        return;
      }

      // Valid item(s)
      this.identifierError = null;
      this.isSerialNumberValid = true;
      this.showValues = true;
      this.validationSearchResults = await this.buildDisplayResults(this.itemsList);
      this.isResultsEmpty = this.validationSearchResults.length === 0;
      this.searchSerialNumberFormGroup.patchValue({ item: this.itemsList[0] });

      this.resetInMemoryPaginatorAndSlice();

    } catch (error) {
      console.error('Error validating serial number', error);

      if (error instanceof HttpErrorResponse && error.error?.code === '40050'){
        this.identifierError = this.translate.instant('SALE_CREATE.SERIAL_NUMBER_ALREADY_SOLD');
      }else{
        this.identifierError = this.translate.instant('SALE_CREATE.ERROR_VALIDATING_SERIAL_NUMBER');
      }

      this.isFormSubmitted = true;
      this.isSerialNumberValid = false;
      this.validationSearchResults = [];
      this.isResultsEmpty = true;
      this.updateInMemoryPageSlice();
    }
  }

  async getProductBySku(sku: string): Promise<ProductDetailsSalesWizardResponse | null> {
    try{
      const product = await firstValueFrom(this.productsService.apiProductsDetailsBySkuSkuGet(sku));

      this.referenceProductRequiresSerialNumber = product?.isSerialNumberRequired ?? true;
      this.product = product;

      return product;
    }
    catch (error){
      console.log('Error fetching product:', error);
      this.product = null;
      return null;
    }
  }

  async onValidateSku() {
    this.currentSku = (this.searchSkuFormGroup.controls['sku'].value || '').toString().trim();
    this.currentItem = null;

    // reset UI state
    this.isFormSubmitted = true;
    this.isSerialNumberValid = false;
    this.showValues = false;
    this.isLimitedEditionValid = false;
    this.displayItemCreationButton = false;
    this.identifierError = null;
    this.validationSearchResults = [];
    this.isResultsEmpty = true;

    this.updateInMemoryPageSlice();

    try {

      // 1) GET products by sku
      const currentProduct = await this.getProductBySku(this.currentSku);
      if (!currentProduct) {
        this.identifierError = this.translate.instant('SALE_CREATE.NO_PRODUCTS_FOUND_WITH_REFERENCE');
        this.isSerialNumberValid = false;
        this.validationSearchResults = [];
        this.isResultsEmpty = true;

        this.updateInMemoryPageSlice();

        return;
      }

      // Check SKU eligibility for implicit activation.
      let isLimitedEdition = false;

      if (this.company.implicitSaleCreation) {
        if (currentProduct.isSerialNumberRequired !== false) {
          this.identifierError = this.translate.instant('SALE_CREATE.SERIAL_NUMBER_REQUIRED_FOR_SKU');
          return;
        }

        const productDetails = await firstValueFrom(this.productsService.apiProductsDetailsIdGet(currentProduct.id));

        isLimitedEdition = productDetails.isLimitedEdition;

        if (!isLimitedEdition) {
          const canCreateSale = await this.permisionService.hasActionPermissions(Actions.create_sales);

          if (!canCreateSale) {
            this.identifierError = this.translate.instant('SALE_CREATE.VALIDATE_SKU');
            return;
          }

          // Keep the product details for review until the item is created.
          const pendingSelection = {
            product: {
              ...productDetails,
              brand: { name: productDetails.brandName }
            },
            pendingItemCreation: {
              productId: productDetails.id,
              warrantyCode: this.itemForm.controls['warrantyCode'].value,
              implicitCodePairing: this.itemForm.controls['implicitCodePairing'].value === 'true'
            }
          };

          const displayResults = await this.buildDisplayResults([pendingSelection]);

          if (displayResults.length === 1) {
            this.onSaveStepData(displayResults[0]);
            return;
          }

          this.showValues = true;
          this.validationSearchResults = displayResults;
          this.resetInMemoryPaginatorAndSlice();
          return;
        }
      }

      // 2) GET items by product.sku
      const payload: GenericRequestByRetailer = {
        request : currentProduct.sku,
        retailerId: this.saleFormService.getStepData('sellerData').retailerId
      };
      const itemsPage = await firstValueFrom(this.itemsService.apiItemsSearchSkuPost(payload));
      const items = itemsPage.results || [];

      if (isLimitedEdition && items.length === 0) {
        this.identifierError = this.translate.instant('SALE_CREATE.VALIDATE_SKU');
        return;
      }

      // 2.2) Items exist -> show list
      if (items.length > 0) {
        this.identifierError = null;
        this.isFormSubmitted = true;

        this.currentItem = null;

        const displayResults = await this.buildDisplayResults(items);

        // if result 1 only -> auto-advance
        if (displayResults.length === 1) {
          this.onSaveStepData(displayResults[0]);
          return;
        }

        // Otherwise show list
        this.isSerialNumberValid = false;
        this.showValues = true;

        this.validationSearchResults = displayResults;
        this.isResultsEmpty = this.validationSearchResults.length === 0;

        this.resetInMemoryPaginatorAndSlice();
        return;
      }

      // 2.1) No items -> decide based on isSerialNumberRequired
      this.isSerialNumberValid = false;
      this.showValues = false;
      this.validationSearchResults = [];
      this.isResultsEmpty = true;
      this.updateInMemoryPageSlice();

      // if SN required -> show create button + modalbx
      if (this.referenceProductRequiresSerialNumber === true) {

        if (this.company.canCreateItemOnSale && (await this.permisionService.hasActionPermissions(Actions.create_sales))) {

          this.identifierError = this.translate.instant('SALE_CREATE.VALIDATE_SKU');

          const productOption: any = {
            id: currentProduct.id,
            text: currentProduct.name || currentProduct.sku || this.currentSku
          };

          this.itemForm.patchValue({
            productId: productOption.id != null ? [productOption.id] : [],
            serialNumber: ''
          });

          this.setItemFormValidatorsForCurrentSaleType();

          this.modalProductOptions = [productOption];
          this.modalProductSelectorDisabled = true;
          this.syncModalProductControlState();
          this.modalSerialDisabled = false;

          this.displayItemCreationButton = true;
        } else {
          this.identifierError = this.translate.instant('SALE_CREATE.VALIDATE_SKU');
        }
        return;
      }

      // If SN NOT required
      if (!(await this.permisionService.hasActionPermissions(Actions.create_sales))) {
        this.identifierError = this.translate.instant('SALE_CREATE.VALIDATE_SKU');
        return;
      }

      let createResp: any;
      if (this.company.isPrepaired) {
        createResp = await firstValueFrom(this.itemsService.apiItemsCreatePrepairedNoSnPost({
          productId: currentProduct.id,
          warrantyCode: this.itemForm.controls['warrantyCode'].value,
          implicitCodePairing: this.itemForm.controls['implicitCodePairing'].value === 'true'
        }));
      } else {
        createResp = await firstValueFrom(this.itemsService.apiItemsCreateNoSnPost({ productId: currentProduct.id }));
      }

      const createdItems: any[] = createResp?.results || [];

      if (!createdItems.length) {
        this.identifierError = this.translate.instant('SALE_CREATE.ERROR_VALIDATING_SKU');
        this.isSerialNumberValid = false;
        this.showValues = false;
        this.validationSearchResults = [];
        this.isResultsEmpty = true;
        this.updateInMemoryPageSlice();
        return;
      }

      // Results: Display the created item
      this.identifierError = null;
      this.isSerialNumberValid = true;
      this.showValues = true;

      const displayResults = await this.buildDisplayResults(createdItems);

      // If 1 result auto-advance
      if (displayResults.length === 1) {
        this.onSaveStepData(displayResults[0]);
        return;
      }

      // Otherwise show list and force user selection
      this.identifierError = null;
      this.isFormSubmitted = true;
      this.showValues = true;
      this.isSerialNumberValid = false;

      this.currentItem = null;

      this.validationSearchResults = displayResults;
      this.isResultsEmpty = this.validationSearchResults.length === 0;

      this.searchSkuFormGroup.patchValue({
        item: null,
        sku: createdItems[0]?.product?.sku || this.currentSku
      });

      this.resetInMemoryPaginatorAndSlice();
      return;

    } catch (error) {
      console.error('Error validating SKU', error);
      this.identifierError = this.translate.instant('SALE_CREATE.ERROR_VALIDATING_SKU');
      this.isFormSubmitted = true;
      this.isSerialNumberValid = false;
      this.validationSearchResults = [];
      this.isResultsEmpty = true;
      this.updateInMemoryPageSlice();
    }
  }

  async loadLimitedEdition(item: any) {
    const selectedEditionId = this.searchSkuFormGroup.controls['editionNumber'].value?.[0];

    if (!selectedEditionId) {
      this.searchSkuFormGroup.get('editionNumber')?.markAsTouched();
      return;
    }

    let filters: any[] = [
      {
        propertyName: 'id',
        operator: FilterOperator.IN,
        values: [selectedEditionId.toString()]
      },
      {
        propertyName: 'Disposition',
        operator: FilterOperator.NOT_IN,
        values: ['SOLD','STOLEN']
      }
    ];

    const filter = {
      pageNumber: 1,
      pageSize: 10,
      filters: filters
    };

    try {
      const itemsPage = await firstValueFrom(this.itemsService.apiItemsFilterPost(filter));
      this.itemsList = itemsPage.results || [];

      // Valid item(s)
      this.identifierError = null;
      this.isSerialNumberValid = true;
      this.validationSearchResults = await this.buildDisplayResults(this.itemsList);
      this.isResultsEmpty = this.validationSearchResults.length === 0;

      this.searchSkuFormGroup.patchValue({ item: this.itemsList[0] });

      this.resetInMemoryPaginatorAndSlice();

    } catch(error) {
      console.error('Error loading limited edition items', error);
    }

    this.showValues = this.itemsList.length > 0;

    if (!this.showValues) {
      this.onSaveStepData(item);
    }
  }

  onSaveStepData(item: any) {
    const productCustomisationId = item?.productCustomisationId ?? item?.productCustomisation?.id ?? null;

    const selectedItem = {
      ...item,
      productCustomisationId
    };

    this.currentItem = selectedItem;

    const basePatch = {
      serialNumber: selectedItem.serialNumber,
      product: selectedItem.product,
      sku: selectedItem.product?.sku,
      item: selectedItem,
      itemId: selectedItem.id,
      productCustomisationId
    };

    // save product metadata in memory
    this.searchSerialNumberFormGroup.patchValue(basePatch);
    this.searchSkuFormGroup.patchValue(basePatch);

    if (this.selectedSaleType == this.saleType.SERIAL_NUMBER) {
      this.saleFormService.setStepData('identifier', this.searchSerialNumberFormGroup.value);
    } else {
      const identifier = this.searchSkuFormGroup.value;

      if (selectedItem.pendingItemCreation) {
        identifier.pendingItemCreation = selectedItem.pendingItemCreation;
      }

      this.saleFormService.setStepData('identifier', identifier);
    }

    this.isSerialNumberValid = true;

    this.stepSaved.emit();
  }

  onInputSerialNumber(event: any) {
    if (this.modalSerialDisabled) return;
    const input = event.target as HTMLInputElement;
    input.value = input.value.trim();
    this.itemForm.controls['serialNumber'].setValue(input.value, { emitEvent: false });
  }

  async onOpenItemModalBox() {
    this.displayItemModalBox = true;

    // reset modal
    this.modalProductSelectorDisabled = false;
    this.modalSerialDisabled = true;
    this.modalProductOptions = [];

    this.setItemFormValidatorsForCurrentSaleType();

    if (this.isReferenceSaleType) {
      // SKU flow: (Product is already known)
      const productId = (this.product as any)?.id;

      const productOption: any = {
        id: productId,
        text: (this.product as any)?.name || (this.product as any)?.sku || this.currentSku
      };

      this.itemForm.patchValue({
        productId: productOption.id != null ? [productOption.id] : [],
        serialNumber: ''
      });

      this.modalProductOptions = [productOption];
      this.modalProductSelectorDisabled = true;
      this.syncModalProductControlState();
      this.modalSerialDisabled = false;

      return;
    }

    // SN flow: load only products available for the retailer selected in step 1
    await this.onLazyLoadComboData('products', {
      search: null,
      pageIndex: 1
    });

    this.modalProductSelectorDisabled = false;
    this.syncModalProductControlState();

    const sn = (this.searchSerialNumberFormGroup.controls['serialNumber'].value || '').toString().trim();

    this.itemForm.patchValue({
      productId: [],
      serialNumber: sn
    });

    this.modalSerialDisabled = true;
  }

  async submitForm() {
    if (!this.itemForm.valid) return;

    try {
      const productIdValue = this.itemForm.controls['productId'].value?.[0];
      const serialNumberValue = (this.itemForm.controls['serialNumber'].value || '').toString().trim();

      const basePayload: any = {
        productId: productIdValue,
        serialNumber: serialNumberValue
      };

      let createResp: any;

      if (this.company.isPrepaired) {
        const payload = {
          ...basePayload,
          warrantyCode: this.itemForm.controls['warrantyCode'].value,
          implicitCodePairing: this.itemForm.controls['implicitCodePairing'].value === 'true'
        };
        createResp = await firstValueFrom(this.itemsService.apiItemsCreatePrepairedPost(payload));
      } else {
        createResp = await firstValueFrom(this.itemsService.apiItemsCreatePost(basePayload));
      }

      const createdId = this.extractCreatedId(createResp);

      this.displayItemModalBox = false;
      this.displayItemCreationButton = false;

      if (this.isReferenceSaleType) {
        // SKU flow: GET item
        if (!createdId) return;

        const createdItem = await firstValueFrom(this.itemsService.apiItemsIdGet(createdId));
        this.identifierError = null;

        const product = createdItem?.product;
        const customisations = product?.productCustomisations ?? [];
        const hasCustomisations = product?.isCustomisable === true && Array.isArray(customisations) && customisations.length > 0;

        // Only 1 customisation -> auto-advance
        if (hasCustomisations && customisations.length === 1) {
          const onlyCustomisation = customisations[0];

          this.onSaveStepData({
            ...createdItem,
            productCustomisationId: onlyCustomisation?.id ?? null,
            productCustomisation: onlyCustomisation ?? null
          });

          return;
        }

        // multiple customisations -> show list
        if (hasCustomisations && customisations.length > 1) {
          this.currentItem = null;

          this.isFormSubmitted = true;
          this.showValues = true;

          this.isSerialNumberValid = false;

          // Customisations list
          this.validationSearchResults = await this.buildDisplayResults([createdItem]);
          this.isResultsEmpty = this.validationSearchResults.length === 0;

          this.searchSkuFormGroup.patchValue({
            sku: createdItem?.product?.sku || this.currentSku,
            item: null
          });

          this.resetInMemoryPaginatorAndSlice();
          return;
        }

        // No customisations: auto-advance
        this.onSaveStepData(createdItem);
        return;
      }

      // SN flow: re-validate SN so list appears
      this.identifierError = null;
      this.searchSerialNumberFormGroup.patchValue({ serialNumber: serialNumberValue });
      await this.onValidateSerialNumber();

    } catch (error) {
      console.error('Error creating item', error);
      this.identifierError = this.translate.instant('SALE_CREATE.ERROR_VALIDATING_SERIAL_NUMBER');
      this.isFormSubmitted = true;
    }
  }

   async getProductAttributesDefinitions() {
      if (!this.productAttributesFeatureEnabled) {
        this.productAttributeDefinitions = [];
        this.translatedProductAttributes = [];
        return;
      }

      try {
        const filter: FilterRequest = {
          pageSize: 500,
          pageNumber: 1
        };

        this.productAttributeDefinitions = (await firstValueFrom(this.productAttributeDefinitionsService.apiProductAttributeDefinitionsFilterDefinitionsOnlyPost(filter))).results ?? [];

      } catch (error) {
        console.log('Error fetching product attributes definition data:', error);
      }
    }

  fillTranslatedAttributeNames() : any{
    this.translatedProductAttributes = this.productAttributeDefinitions?.map(def => {
      const translatedName = def.productAttributeDefinitionTranslations?.find(t => t.isoCode === this.translate.currentLang)?.translatedName
        ?? (def.productAttributeDefinitionTranslations?.find(t => t.isoCode === 'en')?.translatedName ?? def.definitionCode);
      let translatedValue = "-";
      return { code: def.definitionCode, name: translatedName, value: translatedValue, changesOverTime: def.changesOverTime };
    }) ?? [];
  }

  getProductAttributeValue(productCustomisation: ProductCustomisationDetailsResponse,  code: string): string {
    const definition = this.productAttributeDefinitions?.find(
      d => d.definitionCode === code
    );

    if (!definition) {
      return '-';
    }

    const productAttributeValues =
      productCustomisation.productAttributeValues?.filter(
        x => x.productAttributeDefinitionId === definition.id
      ) ?? [];

    const translatedValues = productAttributeValues.map(
      productAttributeValue =>
        productAttributeValue.productAttributeValueTranslations?.find(
          t => t.isoCode === this.translate.currentLang
        )?.translatedValue
        ?? productAttributeValue.productAttributeValueTranslations?.find(
          t => t.isoCode === 'en'
        )?.translatedValue
        ?? productAttributeValue.valueCode
    );

    return translatedValues.length
      ? translatedValues.join(', ')
      : '-';
  }

  get noticeColor(): 'blue' | 'yellow' | 'red' {
    switch (this.noticeType) {
      case NoticeType.INFORMATION:
        return 'blue';
      case NoticeType.WARNING:
        return 'yellow';
      case NoticeType.ALERT:
        return 'red';
      default:
        return 'blue';
    }
  }

  private getSelectedRetailerId(): number | null {
    const sellerData = this.saleFormService.getStepData('sellerData');

    const value = sellerData?.retailerId ?? sellerData?.retailer?.id;
    console.log('Selected retailerId:', value);

    const retailerId = Number(value);

    return retailerId > 0 ? retailerId : null;
  }

  private configureProductsFilterByRetailerBrands(): boolean {
    const retailerId = this.getSelectedRetailerId();

    if (retailerId === null) {
      console.error('Cannot load products because no retailer was selected in the seller-details step.');

      this.combosData['products'].reset();
      this.modalProductOptions = [];

      return false;
    }

    this.combosData['products'].additionalFilters = [
      {
        propertyName: 'brand.retailers.Id',
        operator: FilterOperator.IN,
        values: [retailerId.toString()]
      }
    ];

    return true;
  }
}
